import React from "react";
import "bootstrap/dist/css/bootstrap.css";
import AddressException from "../components/AddressException/AddressException";
import AddressExceptionService from "../services/service.addressException";
import { toast } from "react-toastify";

export class AddressExceptionContainer extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showSpinner: false,
            tableData: [],
            currentPage: 1,
            itemsPerPage: 20,
            searchFFR: "",
            debouncedSearch: "",
            searchTimer: null,
            nextPageToken: null,
            lastPage: true,
            pageTokenHistory: [],
            totalCount: null,
            showUploadHistory: false,
            uploadHistory: [],
            uploadHistoryLoading: false,
            uploadHistoryError: null,
            showDownloadWarning: false,
            activeUploadInfo: null,
        };
    }

    componentDidMount() {
        this.fetchTableData(null, this.state.itemsPerPage, this.state.debouncedSearch);
    }

    componentDidUpdate(prevProps, prevState) {
        // Debounce search input and reset pagination when search changes
        if (prevState.searchFFR !== this.state.searchFFR) {
            if (this.state.searchTimer) {
                clearTimeout(this.state.searchTimer);
            }
            const timer = setTimeout(() => {
                this.setState({ 
                    debouncedSearch: this.state.searchFFR,
                    currentPage: 1,
                    pageTokenHistory: [],
                    nextPageToken: null
                }, () => {
                    this.fetchTableData(null, this.state.itemsPerPage, this.state.debouncedSearch);
                });
            }, 500);
            this.setState({ searchTimer: timer });
        }
    }

    componentWillUnmount() {
        if (this.state.searchTimer) {
            clearTimeout(this.state.searchTimer);
        }
    }

    fetchTableData = async (pageToken = null, pageSize = 20, search = "") => {
        this.setState({ showSpinner: true });
        
        try {
            // GET API call to fetch failed addresses with search parameter
            // Function signature: getFailedAddresses(destination_location_code, search, pageToken, pageSize)
            const response = await AddressExceptionService.getFailedAddresses(
                this.props.dcValue,  // destination_location_code
                search,              // search
                pageToken,           // pageToken
                pageSize             // pageSize
            );
            
            if (response && response.data) {
                const records = response.data.records || [];
                const stateUpdate = {
                    tableData: records,
                    nextPageToken: response.data.nextPageToken,
                    lastPage: response.data.lastPage,
                    showSpinner: false
                };
                // Backend only returns totalCount on first-page requests; preserve cached value on subsequent pages
                if (response.data.totalCount != null) {
                    stateUpdate.totalCount = response.data.totalCount;
                }
                this.setState(stateUpdate);
                
                // Show success message when data is loaded
                if (search && records.length > 0) {
                    toast.success(`Found ${records.length} matching record(s) for "${search}".`);
                } else if (search && records.length === 0) {
                    toast.info(`No records found for "${search}".`);
                }
                return;
            }
        } catch (error) {
            console.error("Error fetching failed addresses:", error);
            this.setState({ showSpinner: false });
            toast.error("Failed to fetch address exceptions. Please try again.");
        }
    };

    handleNextPage = () => {
        const { nextPageToken, currentPage, pageTokenHistory, itemsPerPage, debouncedSearch } = this.state;
        if (nextPageToken) {
            // Store current token in history before moving to next page
            this.setState({
                pageTokenHistory: [...pageTokenHistory, nextPageToken],
                currentPage: currentPage + 1
            }, () => {
                this.fetchTableData(nextPageToken, itemsPerPage, debouncedSearch);
            });
        }
    };

    handlePreviousPage = () => {
        const { currentPage, pageTokenHistory, itemsPerPage, debouncedSearch } = this.state;
        if (currentPage > 1) {
            const newHistory = [...pageTokenHistory];
            newHistory.pop(); // Remove current page token
            const previousToken = newHistory.length > 0 ? newHistory[newHistory.length - 1] : null;
            
            // For going back to first page, use null token
            const tokenToUse = currentPage === 2 ? null : previousToken;
            
            this.setState({
                pageTokenHistory: currentPage === 2 ? [] : newHistory,
                currentPage: currentPage - 1
            }, () => {
                this.fetchTableData(tokenToUse, itemsPerPage, debouncedSearch);
            });
        }
    };

    handleFirstPage = () => {
        const { itemsPerPage, debouncedSearch } = this.state;
        this.setState({
            currentPage: 1,
            pageTokenHistory: [],
            nextPageToken: null
        }, () => {
            this.fetchTableData(null, itemsPerPage, debouncedSearch);
        });
    };

    handleProposedAddressChange = (index, field, value) => {
        const updatedData = [...this.state.tableData];
        updatedData[index].proposed[field] = value;
        this.setState({ tableData: updatedData });
    };

    handleRetryClick = async (ffrNum, proposedAddress) => {
        this.setState({ showSpinner: true });
        
        const payload = {
            ffrNumber: ffrNum,
            shipToAddress: {
                addressLine1Text: proposedAddress.addressLine1 || "",
                addressLine2Text: proposedAddress.addressLine2 || "",
                addressLine3Text: proposedAddress.addressLine3 || "",
                cityName: proposedAddress.city || "",
                stateProvinceCode: proposedAddress.state || "",
                postalCode: proposedAddress.postalCode || ""
            },
            lastUpdatedBy: this.props.loggedInUser || "current_user"
        };
        
        try {
            // POST API call to update address and retry
            const response = await AddressExceptionService.updateRetryAddress(payload);
            toast.warn(<>Retry initiated for FFR Number: <b>{ffrNum}</b>. Address updated and retry triggered.</>);
            // Wait 2 seconds before refreshing table data to allow backend processing
            setTimeout(() => {
                // Reset pagination state and fetch first page
                this.setState({
                    currentPage: 1,
                    pageTokenHistory: [],
                    nextPageToken: null
                }, () => {
                    this.fetchTableData(null, this.state.itemsPerPage, this.state.debouncedSearch);
                });
            }, 2000);
        } catch (error) {
            this.setState({ showSpinner: false });
            console.error("Error updating address:", error);
            const errorMessage = error?.response?.data?.message || "An error occurred while updating address.";
            toast.error(<>Retry failed for FFR Number: <b>{ffrNum}</b>. {errorMessage}</>);
        }
    };

    onSearchFFRChange = (value) => {
        this.setState({ searchFFR: value });
    };

    handleDownloadCsv = async () => {
        this.setState({ showSpinner: true });
        try {
            await AddressExceptionService.downloadFailedAddressesCsv(
                this.props.dcValue,
                this.props.loggedInUser || "system"
            );
            toast.success("CSV downloaded successfully.");
        } catch (err) {
            if (err.message === 'ACTIVE_UPLOAD' && err.activeUploadInfo) {
                // Backend returned 409 — active upload in progress, show warning popup
                this.setState({ showDownloadWarning: true, activeUploadInfo: err.activeUploadInfo });
            } else {
                console.error("Error downloading CSV:", err);
                toast.error("Failed to download CSV. Please try again.");
            }
        } finally {
            this.setState({ showSpinner: false });
        }
    };

    executeDownload = async () => {
        this.setState({ showSpinner: true, showDownloadWarning: false, activeUploadInfo: null });
        try {
            await AddressExceptionService.downloadFailedAddressesCsv(
                this.props.dcValue,
                this.props.loggedInUser || "system",
                true  // force=true bypasses the active-upload guard
            );
            toast.success("CSV downloaded successfully.");
        } catch (error) {
            console.error("Error downloading CSV:", error);
            toast.error("Failed to download CSV. Please try again.");
        } finally {
            this.setState({ showSpinner: false });
        }
    };

    handleCloseDownloadWarning = () => {
        this.setState({ showDownloadWarning: false, activeUploadInfo: null });
    };

    handleUploadCsvClick = () => {
        if (this.fileInputRef) {
            this.fileInputRef.click();
        }
    };

    handleFileSelected = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = null;

        // Validate file type
        const name = file.name || "";
        if (!name.toLowerCase().endsWith(".csv")) {
            toast.error("Please select a CSV file.");
            return;
        }

        this.setState({ showSpinner: true });
        try {
            const response = await AddressExceptionService.bulkRetryUpload(
                file, this.props.dcValue, this.props.loggedInUser || "system"
            );
            const { fileId, totalRecords } = response?.data || {};
            const recordsInfo = totalRecords != null ? ` Processing ${totalRecords} records.` : "";
            const idInfo = fileId ? ` File ID: ${fileId}` : "";
            toast.success(`CSV uploaded successfully.${recordsInfo}${idInfo}`);
            setTimeout(() => {
                this.setState({ currentPage: 1, pageTokenHistory: [], nextPageToken: null }, () => {
                    this.fetchTableData(null, this.state.itemsPerPage, this.state.debouncedSearch);
                });
            }, 2000);
        } catch (error) {
            console.error("Error uploading CSV:", error);
            const status = error?.response?.status;
            const data   = error?.response?.data || {};

            if (status === 409 && data.hasActiveUpload) {
                // Active upload/retry still running — hard block, no "upload anyway" option.
                const fileName   = data.fileName ? `"${data.fileName}"` : "your last upload";
                const statusText = data.status
                    ? data.status.replace(/_/g, " ").toLowerCase()
                    : "in progress";
                toast.error(
                    `Upload blocked: ${fileName} is still ${statusText}. Please try after sometime.`,
                    { autoClose: 8000 }
                );
            } else if (status === 409) {
                const base = data.message || "Duplicate upload detected.";
                toast.error(base);
            } else {
                const msg = data.message || "Failed to upload CSV. Please try again.";
                toast.error(msg);
            }
        } finally {
            this.setState({ showSpinner: false });
        }
    };

    handleOpenHistory = async () => {
        this.setState({ showUploadHistory: true, uploadHistoryLoading: true, uploadHistoryError: null });
        try {
            const data = await AddressExceptionService.getUploadHistory(this.props.dcValue);
            const history = (data && data.history) ? data.history : [];
            this.setState({ uploadHistory: history, uploadHistoryLoading: false, uploadHistoryError: null });
        } catch (err) {
            console.error("Error fetching upload history:", err);
            const status = err?.status || err?.data?.status;
            const msg = status === 404
                ? "Upload history endpoint not found. Please restart the backend service."
                : "Failed to load upload history. Check the backend logs.";
            toast.error(msg);
            this.setState({ uploadHistory: [], uploadHistoryLoading: false, uploadHistoryError: msg });
        }
    };

    handleCloseHistory = () => {
        this.setState({ showUploadHistory: false, uploadHistory: [], uploadHistoryError: null });
    };

    handleRefreshHistoryRow = async (fileId) => {
        try {
            const response = await AddressExceptionService.refreshUploadBatch(fileId);
            // getBatchStatus returns UploadHistoryResponse { history: [...], totalCount: N }
            const updatedItem = (response.history || [])[0];
            if (updatedItem) {
                const prev = this.state.uploadHistory.find(h => h.fileId === fileId);
                const statusChanged = prev && prev.status !== updatedItem.status;
                this.setState(prevState => ({
                    uploadHistory: prevState.uploadHistory.map(h =>
                        h.fileId === fileId ? { ...h, ...updatedItem } : h
                    )
                }));
                if (statusChanged) {
                    toast.success(`Status updated to: ${updatedItem.status}`);
                } else {
                    toast.info("Status unchanged. Processing may still be in progress.");
                }
            } else {
                toast.info("No update received for this batch.");
            }
        } catch (error) {
            console.error("Error refreshing batch:", error);
            toast.error("Failed to refresh batch status.");
        }
    };

    handleDownloadHistoryRow = async (row) => {
        try {
            await AddressExceptionService.downloadFailedRowsCsv(row.fileId, this.props.dcValue);
            toast.success("Failed rows CSV downloaded.");
        } catch (error) {
            console.error("Error downloading failed rows:", error);
            toast.error("Failed to download CSV.");
        }
    };

    render() {
        return (
            <div className="container-fluid">
                <input
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    ref={ref => { this.fileInputRef = ref; }}
                    onChange={this.handleFileSelected}
                />
                <AddressException
                    showSpinner={this.state.showSpinner}
                    tableData={this.state.tableData}
                    currentPage={this.state.currentPage}
                    itemsPerPage={this.state.itemsPerPage}
                    searchFFR={this.state.searchFFR}
                    debouncedSearch={this.state.debouncedSearch}
                    lastPage={this.state.lastPage}
                    totalCount={this.state.totalCount}
                    handleProposedAddressChange={this.handleProposedAddressChange}
                    handleRetryClick={this.handleRetryClick}
                    onSearchFFRChange={this.onSearchFFRChange}
                    onNextPage={this.handleNextPage}
                    onPreviousPage={this.handlePreviousPage}
                    onFirstPage={this.handleFirstPage}
                    loggedInUser={this.props.loggedInUser}
                    dcValue={this.props.dcValue}
                    onDownloadCsv={this.handleDownloadCsv}
                    onUploadCsv={this.handleUploadCsvClick}
                    onOpenHistory={this.handleOpenHistory}
                    showUploadHistory={this.state.showUploadHistory}
                    uploadHistory={this.state.uploadHistory}
                    uploadHistoryLoading={this.state.uploadHistoryLoading}
                    uploadHistoryError={this.state.uploadHistoryError}
                    onCloseHistory={this.handleCloseHistory}
                    onRefreshHistoryRow={this.handleRefreshHistoryRow}
                    onDownloadHistoryRow={this.handleDownloadHistoryRow}
                    showDownloadWarning={this.state.showDownloadWarning}
                    activeUploadInfo={this.state.activeUploadInfo}
                    onCloseDownloadWarning={this.handleCloseDownloadWarning}
                    onDownloadAnyway={this.executeDownload}
                />
            </div>
        );
    }
}

export default AddressExceptionContainer;
